import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from "@/lib/api";
import { defaultTaskCfg } from "@/lib/taskProperties";

// Personal per-view task display preferences (density + visible/ordered
// properties), persisted via the EXISTING /api/preferences store under
// `task_props[viewKey]`. No second preference architecture.
export function useTaskViewPrefs(viewKey, ctx) {
  const [cfg, setCfg] = useState(() => defaultTaskCfg(ctx));
  const ref = useRef({});
  const timer = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    let alive = true;
    setCfg(defaultTaskCfg(ctx));
    (async () => {
      try {
        const prefs = await apiFetch("/preferences");
        ref.current = prefs || {};
        const saved = prefs?.task_props?.[viewKey];
        if (alive && saved) setCfg((c) => ({ ...c, ...saved }));
      } catch { /* keep defaults */ }
    })();
    return () => { alive = false; };
  }, [viewKey, ctx]);

  const update = useCallback((patch) => {
    setCfg((c) => {
      const next = typeof patch === "function" ? patch(c) : { ...c, ...patch };
      clearTimeout(timer.current);
      const task_props = { ...(ref.current.task_props || {}), [viewKey]: next };
      ref.current = { ...ref.current, task_props };
      pending.current = { task_props };
      timer.current = setTimeout(() => {
        pending.current = null;
        apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: { task_props } }) }).catch(() => {});
      }, 500);
      return next;
    });
  }, [viewKey]);

  // Flush any pending change immediately on unmount so a fast navigation/reload
  // never drops the user's last display-preference edit.
  useEffect(() => () => {
    clearTimeout(timer.current);
    if (pending.current) {
      apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: pending.current }) }).catch(() => {});
      pending.current = null;
    }
  }, []);
  return { cfg, update };
}
